import React, { useMemo, useState } from 'react';
import EditUser from 'views/Users/Details/UserDetails/dialogs/EditUser';
import EditType from 'views/Users/Details/UserDetails/dialogs/EditType';
import EditUserAccount from 'views/Users/Details/UserDetails/dialogs/EditUserAccount';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import { translate } from 'components/Translation';
import { BodyMBold, BodyS } from 'components/Typography/Typography';
import EditPermission from 'views/Users/Details/UserDetails/dialogs/EditPermission';
import _ from 'lodash';
import AddPermission from 'views/Users/Details/UserDetails/dialogs/AddPermission';
import ManageBillingAccountPermission from 'views/Users/Details/UserDetails/dialogs/ManageBillingAccountPermission';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { UserType } from 'views/Users/UsersList';
import AddEmailDialog from 'views/Users/UsersList/subcomponents/AddEmailDialog';
import emailNotValid from 'utils/objectsValidation/emailNotValid';

interface IExtendedSingleDepartments extends SingleDepartment {
	permission: types.users.IExtendedSinglePermission;
}

interface IAccountsWithDepartmentsDetails extends SingleCorpAccount {
	permission: types.users.IExtendedSinglePermission;
	departments: IExtendedSingleDepartments[];
}

interface IPreparedPermission extends types.users.IExtendedSinglePermission {
	isUnlimited: boolean;
}

interface IAccountsWithDepartments {
	[accountId: string]: IAccountsWithDepartmentsDetails;
}

interface FormData {
	newEmail: string;
}

interface IDialogsProps {
	dialog: types.users.editDialog;
	setDialog: React.Dispatch<React.SetStateAction<types.users.editDialog>>;
	editPermissionAccount: types.users.IEditPermissionAccount | null;
	setEditPermissionAccount: React.Dispatch<React.SetStateAction<types.users.IEditPermissionAccount | null>>;
	closeDialog: () => void;
	departments: SingleDepartment[];
	accountsWithoutPermissions: SingleCorpAccount[];
	resetEditData: () => void;
	handleDeletePermissionsRequest: () => void;
	accounts: SingleCorpAccount[];
	resetEditType: () => void;
	user: types.users.IExtendedCorpUserDetails;
	handleEditRequest: () => void;
	editData: types.users.IEditData;
	setEditData: React.Dispatch<React.SetStateAction<types.users.IEditData>>;
	editTypeForm: { type: types.users.userType };
	setEditTypeForm: React.Dispatch<React.SetStateAction<{ type: types.users.userType }>>;
	handleSaveAddData: (permissions: types.users.IExtendedSinglePermission[]) => void;
	handleSaveAddBillingAccountPermission: (permissions: types.users.IExtendedSinglePermission[]) => void;
	handleUpdateBillingAccountPermission: (permissions: types.users.IExtendedSinglePermission[]) => void;
	handleEditPermissionRequest: () => void;
	handleEditTypeRequest: () => void;
	handleSaveEditUserAccount: () => void;
	handleSaveEditData: () => void;
	accountsWithDepartments: IAccountsWithDepartments;
	handleAddEmail: (newEmail: string) => void;
}

const Dialogs = ({
	dialog,
	editPermissionAccount,
	closeDialog,
	departments,
	accountsWithoutPermissions,
	resetEditData,
	handleDeletePermissionsRequest,
	setEditPermissionAccount,
	setDialog,
	accounts,
	resetEditType,
	user,
	handleEditRequest,
	editData,
	editTypeForm,
	handleSaveAddData,
	handleSaveAddBillingAccountPermission,
	handleUpdateBillingAccountPermission,
	handleEditPermissionRequest,
	handleEditTypeRequest,
	handleSaveEditUserAccount,
	setEditData,
	setEditTypeForm,
	handleSaveEditData,
	accountsWithDepartments,
	handleAddEmail,
}: IDialogsProps) => {
	const [formData, setFormData] = useState<FormData>({
		newEmail: '',
	});

	const isEnterprise = useMemo(() => accounts.length > 1, [accounts]);
	const isCorporate = !isEnterprise;
	const isManager: boolean = user && user.userData.userType === 'MANAGER';
	const isAdmin: boolean = user && user.userData.userType === 'ADMIN';

	const warningPath = {
		ENDUSER: {
			MANAGER: 'manager',
			ADMIN: 'admin',
		},
		MANAGER: {
			ADMIN: 'admin',
			ENDUSER: 'toEndUser',
		},
		ADMIN: {
			ENDUSER: 'toEndUser',
			MANAGER: 'toManager',
		},
	};

	const departmentIsChecked = (deptId?: string): boolean =>
		!!deptId && deptId in (editPermissionAccount?.departments || []);

	const departmentIsUnlimited = (deptId?: string): boolean =>
		!!deptId &&
		departmentIsChecked(deptId) &&
		!!editPermissionAccount &&
		editPermissionAccount.departments[deptId].isUnlimited;

	const prepareForSetValues = (previousValue: IPreparedPermission, value: string) => {
		let newValue;

		if (value === 'unlimited') {
			newValue = {
				isUnlimited: !previousValue.isUnlimited,
				permissionExpiration: '',
			};
		} else {
			newValue = {
				isUnlimited: false,
				permissionExpiration: value,
			};
		}
		return newValue;
	};

	const handleSaveEditType = (): void => {
		if (user.userData.userType === editTypeForm.type) {
			closeDialog();
		} else if (user.userData.userType === UserType.ENDUSER && !user.userData.email) {
			setDialog('addEmail');
		} else {
			setDialog('editUserTypeConfirmation');
		}
	};

	const accountIsUnlimited = (): boolean =>
		!!editPermissionAccount?.account && editPermissionAccount.account.isUnlimited;

	const setEditDataForm = (name: string, value: string): void => {
		setEditData((editData) => ({ ...editData, [name]: value }));
	};

	const setEditTypeData = (name: string, value: types.users.userType) =>
		setEditTypeForm((data) => ({ ...data, [name]: value }));

	const onChange = (name: keyof FormData, value: string) =>
		setFormData((formDataState) => ({ ...formDataState, [name]: value }));

	const onSubmit = () => {
		handleAddEmail(formData.newEmail);
	};

	const isValid = useMemo(() => {
		return !emailNotValid(formData.newEmail);
	}, [formData]);

	return (
		<>
			<ConditionalRender
				show={dialog === 'editUser'}
				onTrue={() => (
					<EditUser
						handleClose={() => {
							closeDialog();
							resetEditData();
						}}
						formData={editData}
						setFormData={setEditDataForm}
						departments={departments}
						handleSaveData={handleSaveEditData}
						isEnterprise={isEnterprise}
					/>
				)}
			/>
			<ConditionalRender
				show={dialog === 'editUserType'}
				onTrue={() => (
					<EditType
						hideDialog={() => {
							closeDialog();
							resetEditType();
						}}
						formData={editTypeForm}
						setFormData={setEditTypeData}
						handleSave={handleSaveEditType}
						accounts={accounts}
						user={user}
					/>
				)}
			/>
			<ConditionalRender
				show={dialog === 'editUserAccount'}
				onTrue={() => (
					<EditUserAccount
						hideDialog={() => {
							closeDialog();
							resetEditData();
						}}
						accounts={accounts}
						formData={editData}
						setFormData={setEditDataForm}
						departments={departments}
						accountsWithDepartments={accountsWithDepartments}
						handleSaveEditUserAccount={handleSaveEditUserAccount}
					/>
				)}
			/>
			<ConditionalRender
				show={
					dialog === 'editUserAccountDepartmentConfirmation' ||
					dialog === 'editUserAccountAccountsConfirmation'
				}
				onTrue={() => (
					<ConfirmationDialog
						isOpen
						hideDialog={closeDialog}
						header={translate('users.details.changeOrganization')}
						buttons={[
							{
								text: translate('cancel'),
								color: 'tertiary-light',
								onClick: closeDialog,
							},
							{
								text: translate('save'),
								color: 'primary-light',
								onClick: handleEditRequest,
							},
						]}
						dialogBody={
							<>
								<BodyS color="secondary-light">
									{translate('users.details.changeOrganizationSubheader')}
								</BodyS>
								<BodyMBold color="danger">
									{translate('users.details.changeOrganizationWarning')}
								</BodyMBold>
							</>
						}
					/>
				)}
			/>
			<ConditionalRender
				show={dialog === 'editUserTypeConfirmation'}
				onTrue={() => (
					<ConfirmationDialog
						isOpen={dialog === 'editUserTypeConfirmation'}
						hideDialog={() => {
							closeDialog();
							resetEditType();
						}}
						buttons={[
							{
								text: translate('cancel'),
								onClick: () => {
									closeDialog();
									resetEditType();
								},
								color: 'tertiary-light',
							},
							{
								text: translate('change'),
								onClick: handleEditTypeRequest,
								color: 'primary-light',
							},
						]}
						header={translate('users.details.changeUserTypeHeader')}
						dialogBody={
							<>
								<BodyS color="secondary-light">
									{translate(
										'users.details.changeUserTypeSubheader',
										translate(`role.${user.userData.userType}`).toLowerCase(),
										translate(`role.${editTypeForm.type}`).toLowerCase()
									)}
								</BodyS>
								<BodyMBold color="danger">
									{translate(
										`users.details.changeUserTypeWarning.${
											warningPath[user.userData.userType][editTypeForm.type]
										}`
									)}
								</BodyMBold>
							</>
						}
					/>
				)}
			/>
			<ConditionalRender
				show={dialog === 'setUserDepartmentConfirmation'}
				onTrue={() => (
					<ConfirmationDialog
						isOpen
						hideDialog={() => {
							closeDialog();
							resetEditData();
						}}
						buttons={[
							{
								text: translate('cancel'),
								onClick: () => {
									closeDialog();
									resetEditData();
								},
								color: 'tertiary-light',
							},
							{
								text: translate('assign'),
								onClick: handleEditRequest,
								color: 'primary-light',
							},
						]}
						header={translate('user.details.set.department')}
						dialogBody={
							<>
								<BodyS color="secondary-light">
									{translate('user.details.set.department.areYouSure')}
								</BodyS>
								<BodyMBold color="danger">{translate('user.details.set.department.warning')}</BodyMBold>
							</>
						}
					/>
				)}
			/>
			<ConditionalRender
				show={dialog === 'editUserDepartmentConfirmation'}
				onTrue={() => (
					<ConfirmationDialog
						isOpen={dialog === 'editUserDepartmentConfirmation'}
						hideDialog={() => {
							closeDialog();
							resetEditData();
						}}
						buttons={[
							{
								text: translate('cancel'),
								onClick: () => {
									closeDialog();
									resetEditData();
								},
								color: 'tertiary-light',
							},
							{
								text: translate('change'),
								onClick: handleEditRequest,
								color: 'primary-light',
							},
						]}
						header={translate('number.details.change.department')}
						dialogBody={
							<>
								<BodyS color="secondary-light">
									{translate('users.details.changeDepartmentAccept')}
								</BodyS>
								<BodyMBold color="danger">
									{translate('users.details.changeDepartmentWarning')}
								</BodyMBold>
							</>
						}
					/>
				)}
			/>
			<ConditionalRender
				show={dialog === 'editPermission'}
				onTrue={() => (
					<EditPermission
						handleSave={handleEditPermissionRequest}
						hideDialog={() => closeDialog()}
						isEnterprise={isEnterprise}
						account={accountsWithDepartments[editPermissionAccount!.accountId]}
						data={editPermissionAccount}
						accountFn={{
							handleIsUnlimited: accountIsUnlimited,
							handleGetDate: () =>
								editPermissionAccount?.account && editPermissionAccount.account.permissionExpiration,
							handleSetDate: (value) =>
								setEditPermissionAccount((editPermissionAccount) => {
									const newValue = _.cloneDeep(editPermissionAccount);
									if (newValue) {
										_.set(newValue, 'account', {
											...editPermissionAccount!.account,
											...prepareForSetValues(editPermissionAccount!.account, value),
										});
									}
									return newValue;
								}),
						}}
						departmentsFn={{
							handleIsChecked: departmentIsChecked,
							handleBadgeClick: (deptId, name, uiColor) => {
								setEditPermissionAccount((editPermissionAccount) => {
									const newValue = _.cloneDeep(editPermissionAccount);
									if (newValue) {
										if (deptId in (editPermissionAccount?.departments || [])) {
											delete newValue.departments[deptId];
										} else {
											_.set(newValue, `departments[${deptId}]`, {
												departmentId: deptId,
												accountId: editPermissionAccount?.accountId,
												type: 'D',
												isUnlimited: true,
												departmentUiColor: uiColor,
												departmentName: name,
											});
										}
									}
									return newValue;
								});
							},
							handleIsUnlimited: (deptId?: string) =>
								departmentIsChecked(deptId) &&
								!!deptId &&
								editPermissionAccount!.departments[deptId].isUnlimited,
							handleGetDate: (deptId?: string) => {
								if (departmentIsChecked(deptId) && !departmentIsUnlimited(deptId) && !!deptId) {
									return editPermissionAccount!.departments[deptId].permissionExpiration || '';
								}
								return '';
							},
							handleSetDate: (deptId, value) =>
								setEditPermissionAccount((editPermissionAccount) => {
									const oldValue = _.get(editPermissionAccount, `departments.${deptId}`);
									const newValue = _.cloneDeep(editPermissionAccount);
									if (newValue) {
										_.set(newValue, `departments.${deptId}`, {
											...oldValue,
											...prepareForSetValues(oldValue, value),
										});
									}
									return newValue;
								}),
						}}
					/>
				)}
			/>
			<ConditionalRender
				show={dialog === 'addPermission'}
				onTrue={() => (
					<AddPermission
						isEnterprise={isEnterprise}
						isManager={isManager}
						isAdmin={isAdmin}
						isCorporate={isCorporate}
						hideDialog={() => closeDialog()}
						accounts={accountsWithoutPermissions.map((account) => ({
							id: account.accountId,
							name: account.accountName,
							adminsCount: account.adminsCount,
						}))}
						departments={departments}
						handleSaveData={handleSaveAddData}
					/>
				)}
			/>
			<ConditionalRender
				show={dialog === 'addBillingAccountPermission'}
				onTrue={() => (
					<ManageBillingAccountPermission
						hideDialog={() => closeDialog()}
						persistedPermissions={user.permissions}
						handleSaveData={handleSaveAddBillingAccountPermission}
						handleUpdateData={handleUpdateBillingAccountPermission}
					/>
				)}
			/>
			<ConditionalRender
				show={dialog === 'deletePermissionsConfirmation'}
				onTrue={() => (
					<ConfirmationDialog
						dialogBody={translate('users.details.deletePermissionsSubheader')}
						hideDialog={closeDialog}
						header={translate('users.details.deletePermissions')}
						buttons={[
							{
								text: translate('cancel'),
								color: 'tertiary-light',
								onClick: closeDialog,
							},
							{
								text: translate('delete.without.number'),
								color: 'primary-light',
								onClick: handleDeletePermissionsRequest,
							},
						]}
						isOpen
					/>
				)}
			/>
			<ConditionalRender
				show={dialog === 'addEmail'}
				onTrue={() => (
					<AddEmailDialog
						hideDialog={closeDialog}
						isDialogOpen={true}
						formData={formData}
						onChange={onChange}
						onSubmit={onSubmit}
						isValid={isValid}
					/>
				)}
			/>
		</>
	);
};

export default Dialogs;
